import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import DictableTextarea from '@/components/DictableTextarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, ShowerHead, Activity, Scale, NotebookPen, FolderOpen, Check } from 'lucide-react';
import { toast } from 'sonner';

const OGGI = () => new Date().toISOString().slice(0, 10);
const turnoCorrente = () => {
  const h = new Date().getHours();
  return h >= 6 && h < 14 ? 'mattina' : h >= 14 && h < 22 ? 'pomeriggio' : 'notte';
};
const ALVO = [
  ['P', 'Evacuazione'], ['D', 'Diarrea'], ['S', 'Assente'], ['C', 'Clistere'],
];
const PARAMETRI = [
  ['PA_sistolica', 'PA sist.', 'mmHg'], ['PA_diastolica', 'PA diast.', 'mmHg'],
  ['FC', 'FC', 'bpm'], ['FR', 'FR', 'atti/min'], ['SpO2', 'SpO2', '%'],
  ['temperatura', 'Temp.', '°C'], ['glicemia', 'Glicemia', 'mg/dl'],
];
const COSCIENZA = ['A - Vigile', 'C - Confuso', 'V - Risponde alla voce', 'P - Risponde al dolore', 'U - Non risponde'];
const OSSIGENO = ['Aria ambiente', 'O2 occhialini', 'O2 maschera', 'O2 Venturi', 'O2 alti flussi'];
const NEWS2_STILE = { rosso: 'bg-red-100 text-red-700', giallo: 'bg-amber-100 text-amber-700', verde: 'bg-emerald-100 text-emerald-700' };

export default function RegistrazioneLetto() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ospite, setOspite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alvoFatto, setAlvoFatto] = useState('');
  const [docciaFatta, setDocciaFatta] = useState(false);
  const [par, setPar] = useState({});
  const [coscienza, setCoscienza] = useState(COSCIENZA[0]);
  const [ossigeno, setOssigeno] = useState(OSSIGENO[0]);
  const [news2, setNews2] = useState(null);
  const [peso, setPeso] = useState('');
  const [nota, setNota] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try { setOspite((await api.get(`/ospiti/${id}`)).data); }
    catch { toast.error('Ospite non trovato'); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const registraAlvo = async (valore) => {
    setBusy('alvo');
    try {
      await api.put('/moduli/alvo', { ospite_id: id, data: OGGI(), valore });
      setAlvoFatto(valore); toast.success('Alvo registrato');
    } catch { toast.error('Errore registrazione alvo'); }
    setBusy('');
  };

  const registraDoccia = async () => {
    setBusy('doccia');
    try {
      await api.post('/moduli/docce/esegui', { ospite_id: id, data: OGGI() });
      setDocciaFatta(true); toast.success('Doccia registrata');
    } catch { toast.error('Errore registrazione doccia'); }
    setBusy('');
  };

  const salvaParametri = async () => {
    const voci = PARAMETRI.filter(([k]) => par[k]);
    if (!voci.length) { toast.error('Inserisci almeno un valore'); return; }
    setBusy('parametri');
    const adesso = new Date().toISOString();
    try {
      for (const [k, , unita] of voci) {
        await api.post('/parametri', {
          ospite_id: id, tipo: k, valore: parseFloat(par[k]), unita,
          data_rilevazione: adesso,
        });
      }
      for (const [tipo, valore_testo] of [['coscienza', coscienza], ['ossigeno', ossigeno]]) {
        await api.post('/parametri', {
          ospite_id: id, tipo, valore: 0, valore_testo, unita: '', data_rilevazione: adesso,
        });
      }
      toast.success(`${voci.length + 2} parametri registrati`);
      setPar({});
      try { setNews2((await api.get(`/alert/news2/${id}`)).data); } catch { /* opzionale */ }
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore registrazione parametri'); }
    setBusy('');
  };

  const salvaPeso = async () => {
    if (!peso) return;
    setBusy('peso');
    try {
      await api.post('/analisi/rilevazione-pesi', {
        data_rilevazione: OGGI(), rilevazioni: [{ ospite_id: id, peso: parseFloat(peso) }],
      });
      toast.success('Peso registrato'); setPeso('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore registrazione peso'); }
    setBusy('');
  };

  const salvaNota = async () => {
    if (!nota.trim()) return;
    setBusy('nota');
    try {
      await api.post('/diario', { ospite_id: id, data: OGGI(), turno: turnoCorrente(), contenuto: nota, tags: ['al_letto'] });
      toast.success('Nota salvata'); setNota('');
    } catch { toast.error('Errore salvataggio nota'); }
    setBusy('');
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;
  if (!ospite) return <p className="p-6 text-sm text-slate-500">Ospite non trovato.</p>;

  return (
    <div className="max-w-md mx-auto space-y-3 pb-10" data-testid="registrazione-letto-page">
      <div className="rounded-xl bg-brand p-4 text-white">
        <p className="text-[10px] uppercase tracking-widest opacity-80">Registrazione al letto</p>
        <h1 className="text-xl font-semibold font-[Cinzel] leading-tight">{ospite.cognome} {ospite.nome}</h1>
        <p className="text-xs opacity-90 mt-0.5">
          Stanza {ospite.stanza || '-'} · letto {ospite.letto || '-'}{ospite.area ? ` · area ${ospite.area}` : ''}
        </p>
        <Button size="sm" variant="secondary" className="mt-3 h-8 text-xs"
          onClick={() => navigate(`/ospiti/${id}`)} data-testid="apri-cartella-btn">
          <FolderOpen className="w-3.5 h-3.5 mr-1" />Cartella completa
        </Button>
      </div>

      <Card data-testid="card-alvo">
        <CardContent className="p-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Alvo</p>
          <div className="grid grid-cols-4 gap-1.5">
            {ALVO.map(([code, label]) => (
              <Button key={code} size="sm" variant={alvoFatto === code ? 'default' : 'outline'}
                className={`h-12 flex-col gap-0.5 ${alvoFatto === code ? 'bg-brand text-white' : ''}`}
                disabled={busy === 'alvo'} onClick={() => registraAlvo(code)} data-testid={`alvo-${code}`}>
                <span className="text-sm font-bold">{code}</span>
                <span className="text-[9px] font-normal">{label}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-doccia">
        <CardContent className="p-3 flex items-center justify-between">
          <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <ShowerHead className="w-4 h-4 text-brand" />Igiene / doccia
          </p>
          <Button size="sm" variant={docciaFatta ? 'default' : 'outline'} className={docciaFatta ? 'bg-brand text-white h-9' : 'h-9'}
            disabled={busy === 'doccia'} onClick={registraDoccia} data-testid="doccia-btn">
            {docciaFatta ? <><Check className="w-4 h-4 mr-1" />Registrata</> : 'Registra doccia'}
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="card-parametri">
        <CardContent className="p-3 space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5 text-brand" />Parametri vitali
          </p>
          <div className="grid grid-cols-3 gap-2">
            {PARAMETRI.map(([k, label, unita]) => (
              <div key={k}>
                <p className="text-[10px] text-slate-500">{label}</p>
                <Input type="number" step="0.1" inputMode="decimal" className="h-10 text-center"
                  value={par[k] || ''} onChange={e => setPar(p => ({ ...p, [k]: e.target.value }))}
                  data-testid={`par-${k}`} />
              </div>
            ))}
          </div>
          <div>
            <p className="text-[10px] text-slate-500 mb-1">Stato di coscienza (AVPU)</p>
            <div className="grid grid-cols-5 gap-1">
              {COSCIENZA.map(opt => (
                <button key={opt} type="button" onClick={() => setCoscienza(opt)}
                  className={`h-9 rounded-md border text-sm font-bold transition-colors ${coscienza === opt ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-slate-500 border-slate-200'}`}
                  data-testid={`letto-coscienza-${opt[0].toLowerCase()}`}>{opt[0]}</button>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-1">{coscienza}</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-500 mb-1">Ossigeno</p>
            <div className="flex flex-wrap gap-1">
              {OSSIGENO.map(opt => (
                <button key={opt} type="button" onClick={() => setOssigeno(opt)}
                  className={`px-2.5 h-9 rounded-md border text-[11px] font-medium transition-colors ${ossigeno === opt ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-500 border-slate-200'}`}
                  data-testid={`letto-ossigeno-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{opt}</button>
              ))}
            </div>
          </div>
          <Button size="sm" className="w-full h-10 bg-brand hover:bg-brand-dark text-white"
            disabled={busy === 'parametri'} onClick={salvaParametri} data-testid="salva-parametri-btn">
            {busy === 'parametri' ? 'Salvataggio...' : 'Salva parametri'}
          </Button>
          {news2?.calcolabile && (
            <div className={`rounded-lg p-2.5 text-xs ${NEWS2_STILE[news2.livello]}`} data-testid="letto-news2">
              <span className="font-bold">NEWS2 {news2.punteggio}</span> · {news2.indicazione}
              {news2.parziale && <span className="block opacity-80 mt-0.5">Mancano: {news2.mancanti.join(', ')}</span>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="card-peso">
        <CardContent className="p-3 flex items-end gap-2">
          <div className="flex-1">
            <p className="text-[10px] text-slate-500 flex items-center gap-1"><Scale className="w-3 h-3" />Peso (kg)</p>
            <Input type="number" step="0.1" inputMode="decimal" className="h-10" value={peso}
              onChange={e => setPeso(e.target.value)} data-testid="peso-input" />
          </div>
          <Button size="sm" className="h-10 bg-brand hover:bg-brand-dark text-white"
            disabled={busy === 'peso'} onClick={salvaPeso} data-testid="salva-peso-btn">Salva</Button>
        </CardContent>
      </Card>

      <Card data-testid="card-nota">
        <CardContent className="p-3 space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <NotebookPen className="w-3.5 h-3.5 text-brand" />Nota rapida
          </p>
          <DictableTextarea rows={3} value={nota} onChange={e => setNota(e.target.value)}
            placeholder="Osservazione da lasciare in cartella" data-testid="nota-input" />
          <Button size="sm" className="w-full h-10 bg-brand hover:bg-brand-dark text-white"
            disabled={busy === 'nota'} onClick={salvaNota} data-testid="salva-nota-btn">Salva nota in diario</Button>
        </CardContent>
      </Card>

      <p className="text-[10px] text-center text-slate-400">
        Ogni registrazione viene firmata automaticamente con il tuo nome e l'orario.
      </p>
      <Badge variant="outline" className="mx-auto block w-fit text-[10px]">{OGGI().split('-').reverse().join('/')}</Badge>
    </div>
  );
}
